import { waitlistStatusText } from '@/pages/CollaboratePage/Logic'
import { useVideoOrder, useViewMode } from '@/pages/SessionPage/internal/shared/useViewMode'
import { MusePeer } from '@/services/RoomContextMusePeer'
import { RoomContext, RoomContextType, useRoomContext } from '@/RoomContext'
import { cn } from '@/utlis/cn'
import { useContext, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { env } from '@/services/EnvironmentVariables'
import useRoomViews from '@/services/useRoomViews'
import { Button } from '@/UIComponents2/Button'
import { UserContext, UserContextType } from '@/UserContext'
import OptionsEllipsesIcon from '@/CustomIcons/OptionsEllipsesIcon'
import useMiscRoomActions from '@/services/useMiscRoomActions'
import { UserCard } from './UserCard/UserCard'
import { sidebarVirtualizationHeightCache, useSidebarVirtualizationService } from './SidebarVirtualization'
import { classifyAndSortPeers } from './SidebarLogic'
import useRoomActions, { joinRoomState } from '@/services/useRoomActions'
import { Username } from '@/components/Username'
import { SimpleMenu } from '@/UIComponents2/SimpleMenu'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import useAnalytics from '@/services/useAnalytics'

export default function UserList() {
    const { state: userState } = useContext(UserContext) as UserContextType
    const { state: roomState } = useContext(RoomContext) as RoomContextType
    const viewMode = useViewMode()
    const videoOrder = useVideoOrder()

    const { normalPeers, listenOnlyPeers } = useMemo(() => {
        return classifyAndSortPeers(Object.values(roomState.peers), videoOrder)
    }, [roomState.peers, videoOrder])

    const { divRef, onScroll, deriveNormalPeerVisibilityMap } = useSidebarVirtualizationService()

    const normalPeerVisibilityMap = useMemo(
        () => deriveNormalPeerVisibilityMap(normalPeers),
        [deriveNormalPeerVisibilityMap, normalPeers]
    )

    return (
        <div
            id="user-list-scroll-container"
            ref={divRef}
            onScroll={onScroll}
            className={cn(
                'flex-col gap-2 overflow-y-auto overflow-x-hidden flex-1 min-h-0 pr-2 md:pr-0',
                viewMode.sidebarCollapsed ? 'hidden' : 'flex'
            )}
            style={{
                display: viewMode.sidebarCollapsed ? 'none' : 'flex',
            }}
        >
            <WaitlistIndicator />

            {!joinRoomState.joiningRoom && userState.clientType !== 'listenonly' && (
                <UserCard
                    user="me"
                    showCamera={viewMode.showVideosInSidebar}
                    isVirtualized={false}
                    onHeightChanged={() => {}}
                />
            )}
            <PeersSection normalPeers={normalPeers} normalPeerVisibilityMap={normalPeerVisibilityMap} />

            {listenOnlyPeers.length > 0 && <ListenOnlyPeersSection listenOnlyPeers={listenOnlyPeers} />}
        </div>
    )
}

function PeersSection({
    normalPeers,
    normalPeerVisibilityMap,
}: {
    normalPeers: MusePeer[]
    normalPeerVisibilityMap: Record<string, boolean>
}) {
    const viewMode = useViewMode()

    return (
        <>
            {normalPeers.map((peer, i) => {
                // note: use `i` because order is relevant in deciding visibility
                if (env.TEST_LIMIT_SIDEBAR_PEER_CT) {
                    if (i >= parseInt(env.TEST_LIMIT_SIDEBAR_PEER_CT)) {
                        return null
                    }
                }

                // need to reload the component when the sidebar mode changes;
                // used to detect if the component fully loaded.
                const key = `PeerSidebarVideo_${peer.sessionId}_${i}_galleryHidden=${viewMode.galleryHidden}_poppedOut=${viewMode.sidebarPoppedOut}`

                if (normalPeerVisibilityMap[peer.sessionId]) {
                    return (
                        <UserCard
                            key={key}
                            user={peer}
                            showCamera={viewMode.showVideosInSidebar}
                            isVirtualized={false}
                            onHeightChanged={(height: number) => {
                                sidebarVirtualizationHeightCache.set(peer.sessionId, height)
                            }}
                        />
                    )
                } else {
                    return (
                        <UserCard
                            key={key}
                            user={peer}
                            showCamera={viewMode.showVideosInSidebar}
                            isVirtualized={true}
                            virtualizedHeight={sidebarVirtualizationHeightCache.get(peer.sessionId)}
                        />
                    )
                }
            })}
        </>
    )
}

function WaitlistIndicator() {
    const { t } = useTranslation()
    const [roomState] = useRoomContext()
    const { openWaitingRoomDialog } = useMiscRoomActions()
    const { collaboratorCount } = useRoomViews()

    const count = roomState.roomWaitlist.length
    if (roomState.isHost && count > 0) {
        return (
            <Button
                variant="primary"
                className="w-full py-[7px] inline rounded-[12px]"
                onClick={() => openWaitingRoomDialog()}
            >
                <Trans i18nKey="There is <1>{{count}} person</1> in the waiting room" count={count}>
                    There are <span className="font-bold">{'{{count}}'} people</span> in the waiting room
                </Trans>
            </Button>
        )
    }

    return collaboratorCount === 1 ? (
        <div className="w-full text-center text-muse-yellow p-[7px] bg-white/5 rounded-[12px] shadow-glow border-muse-toplit-low">
            {waitlistStatusText(t, roomState.myWaitlistStatus)}
        </div>
    ) : null
}

function ListenOnlyPeersSection({ listenOnlyPeers }: { listenOnlyPeers: MusePeer[] }) {
    const { t } = useTranslation()
    const { startRemovePeer } = useRoomActions()
    const { logUserActivityWithContext } = useAnalytics()
    const [roomState] = useRoomContext()
    const isHost = roomState.isHost

    return (
        <div className="w-full pt-[12px]">
            <div className="w-full opacity-50">
                {t('Listeners - ')} {listenOnlyPeers.length}
            </div>
            <div className="w-full flex flex-col pt-[12px] gap-1">
                {listenOnlyPeers.map((peer: MusePeer) => (
                    <div className="px-4 py-2 flex rounded-[12px] bg-[rgba(255,255,255,0.08)]">
                        <div className="flex gap-[12px] grow">
                            <div className="h-full flex items-center overflow-hidden text-ellipsis size-[16px] grow">
                                <Username peer={peer} />
                            </div>
                        </div>
                        {isHost && (
                            <SimpleMenu
                                items={[
                                    {
                                        label: t('Invite to stage'),
                                        onClick: () => {},
                                        className: 'cursor-not-allowed opacity-50',
                                    },
                                    {
                                        label: t('Shadow remove'),
                                        onClick: () => {},
                                        className: 'text-muse-danger cursor-not-allowed opacity-50',
                                    },
                                    {
                                        label: t('Remove from session'),
                                        onClick: () => {
                                            startRemovePeer(peer.sessionId)
                                            logUserActivityWithContext(AnalyticsEvents.CLICKED_REMOVE_USER_BUTTON)
                                        },
                                        className: 'text-muse-danger',
                                    },
                                ]}
                            >
                                <Button className="opacity-80 hover:opacity-100">
                                    <OptionsEllipsesIcon style={{ fontSize: '1.3rem' }} />
                                </Button>
                            </SimpleMenu>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}
